/**
 * The delivery model as one contract runs it: the version and profile it is on, the
 * parameters it sets, and the cadences the profile calls for. Reached from the contract
 * page, beneath the skills switch.
 *
 * Nothing here changes the model. The parameters are the contract's own values over the
 * model's defaults, and a cadence switch stops a cadence on this contract alone; both go
 * to the routes the rail and Structure already used.
 */
import { useState } from "preact/hooks";
import { cap, fmtDayFull, plural } from "@trellis/core";
import type { ProgramView } from "@trellis/core";
import type { PickerModel } from "./program.js";
import type { StructurePage, UnitCadenceLine } from "./structure.js";
import { Link } from "../components/link.js";
import { Picker } from "../components/picker.js";

const label = (id: string) => cap(id.replace(/-/g, " "));
const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
/** ISO order, Monday first, so the index into this is the weekday number minus one. */
const SHORT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

type Parameter = PickerModel["parameters"][number];

/** What a program admin may say about one cadence on one unit. */
export interface CadenceEdit {
  weekdays?: number[] | null;
  link?: { kind: "teams-meeting"; joinWebUrl: string; organiserUpn: string } | { kind: "external"; sourceUrl: string } | null;
}

/** One other name a person on the roster is known by. `directory` rows are read only here. */
export interface PersonIdentity {
  kind: "email" | "eua" | "entra-oid";
  value: string;
  upn: string;
  source: "directory" | "lead";
  statedBy: string | null;
  statedAt: string;
}

export interface IdentitiesView {
  identities: PersonIdentity[];
  roster: { upn: string; displayName: string }[];
}

export interface ModelScreenProps {
  view: ProgramView;
  model: PickerModel;
  onParameters: (parameters: Record<string, unknown>) => Promise<void>;
  /** Absent when the read failed or the viewer may not state one; the panel is then not drawn. */
  identities?: IdentitiesView | undefined;
  /** Cadences switched off across the whole contract. Absent when the read behind them failed. */
  programOptOuts?: string[] | undefined;
  onOptOut?: ((cadenceId: string, optedOut: boolean) => Promise<void>) | undefined;
  /** The per-unit half of the same read: which units are configured, and how. */
  structure?: Pick<StructurePage, "eligibleCadences" | "unitCadences" | "optOuts" | "meetingsOptIn" | "people"> | undefined;
  onUnitCadence?: ((unitId: string, cadenceId: string, edit: CadenceEdit) => Promise<void>) | undefined;
  notice?: { text: string; error?: boolean } | undefined;
}

export function ModelScreen({ view, model, onParameters, identities, programOptOuts, onOptOut, structure, onUnitCadence, notice }: ModelScreenProps) {
  const profile = model.profiles.find((p) => p.id === view.header.profile);
  const params = (profile?.parameterIds ?? [])
    .map((id) => model.parameters.find((p) => p.id === id))
    .filter((p): p is Parameter => !!p);
  const at = `/app/programs/${encodeURIComponent(view.programId)}`;
  return (
    <main class="main settings">
      <div class="page-head">
        <span class="label"><Link class="link" href={at}>{view.header.name}</Link> · delivery model</span>
        <h1 class="h-lg">Delivery model</h1>
        <p class="lede">
          {model.name} v{view.header.modelVersion}, {view.header.profileName} profile.
          {profile?.description ? ` ${profile.description}` : ""} What this contract sets is here; the
          model itself is the same on every contract.
        </p>
        {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}
      </div>

      <section class="panel">
        <div class="panel-head"><span class="label">What this contract is running</span></div>
        <div class="line">
          <span class="dot navy" />
          <span class="body">Version
            <div class="s">v{view.header.modelVersion}{model.version !== view.header.modelVersion ? ` · the model now ships v${model.version}` : ""}</div>
          </span>
        </div>
        <div class="line">
          <span class="dot navy" />
          <span class="body">Profile
            <div class="s">{view.header.profileName} · {plural(profile?.cadences.length ?? 0, "cadence")} · {plural(params.length, "parameter")}</div>
          </span>
        </div>
      </section>

      <Parameters view={view} params={params} model={model} profileName={view.header.profileName} onSave={onParameters} />

      <section class="panel">
        <div class="panel-head"><span class="label">Cadences · what the model says happens</span></div>
        {view.cadences.length === 0 && view.unitCadences.length === 0 && (
          <div class="line"><span class="dot off" /><span class="body muted">No cadences in this profile.</span></div>
        )}
        {view.cadences.map((c) => (
          <div class="line" key={c.cadenceId}>
            <span class={`dot ${c.tone}`} />
            <span class="body">{c.frequency}: {c.name}
              <div class="s">{c.holder ? `${c.runsBy}: ${c.holder}` : `${c.runsBy}: nobody yet`}{view.unitCadences.length ? " · on the contract" : ""}</div>
            </span>
          </div>
        ))}
        {view.unitCadences.map((c) => {
          const off = programOptOuts?.includes(c.cadenceId) ?? false;
          const def = structure ? defOf(structure, c.cadenceId) : undefined;
          const unconfigured = def?.needsLink && !off ? unconfiguredCount(view, structure, c.cadenceId) : 0;
          return (
            <div key={c.cadenceId}>
              <div class="line">
                <span class={`dot ${off ? "off" : unconfigured || c.missed || c.unfilled ? "watch" : "navy"}`} />
                <span class="body">{c.name}
                  <div class="s">On each unit · {off ? "off for the whole contract" : `${plural(c.eligible, "unit")} eligible`}</div>
                  {/* A cadence closed by evidence Trellis has to find needs somewhere to look. Until it
                      has one, nothing opens on that unit, so the number goes here and not into a
                      finding against each unit's lead. */}
                  {unconfigured > 0 && (
                    <div class="s">{plural(unconfigured, "unit")} with nowhere to look for evidence. Trellis opens nothing there until one is set.</div>
                  )}
                  {onOptOut && programOptOuts && (
                    <label class="s">
                      <input type="checkbox" checked={off} onChange={(e) => void onOptOut(c.cadenceId, (e.target as HTMLInputElement).checked)} />
                      {" "}Off for every unit on this contract
                    </label>
                  )}
                </span>
              </div>
              {!off && structure && onUnitCadence && def && (
                <UnitCadences view={view} cadence={def} structure={structure} onSave={onUnitCadence} />
              )}
            </div>
          );
        })}
        {view.unitCadences.length > 0 && (
          <div class="s">A unit can still switch one of these off on its own account, on <Link class="link" href={`${at}/structure`}>Structure</Link>.</div>
        )}
      </section>

      {identities && <Identities view={identities} programId={view.programId} />}
    </main>
  );
}

/**
 * An address the directory asserts is a Samtek alias; one a lead states is the customer's. Same
 * kind, different thing, and calling a samtek-inc.com address a customer address on the page would
 * teach an admin the wrong rule about who may set what.
 */
function kindLabel(i: PersonIdentity): string {
  if (i.kind === "eua") return "EUA username";
  if (i.kind === "entra-oid") return "Directory object";
  return i.source === "directory" ? "Samtek alias" : "Customer address";
}

/**
 * The other names the people on this contract are known by, as one list. Anything read under one of
 * these lands on the single record, so this decides whose work is whose.
 *
 * A roll-up, not an editor. Stating a name is done on the person, where their username on each
 * linked system sits beside it and an EUA name cannot be typed a second time
 * (docs/2026-09-27-person-identity-page.md).
 */
function Identities({ view, programId }: { view: IdentitiesView; programId: string }) {
  const nameOf = (u: string) => view.roster.find((r) => r.upn === u)?.displayName ?? u;
  const hrefOf = (u: string) => `/app/programs/${encodeURIComponent(programId)}/roster/${encodeURIComponent(u)}`;
  const sorted = [...view.identities].sort((a, b) => nameOf(a.upn).localeCompare(nameOf(b.upn)) || a.value.localeCompare(b.value));
  return (
    <section class="panel">
      <div class="panel-head"><span class="label">Other names for people on this contract</span></div>
      <div class="s">
        Anything Trellis reads under one of these goes onto that person’s single record. It does not
        change who counts as customer personnel: an ask is still an ask because the customer register
        says who the customer is.
      </div>
      {sorted.length === 0 && (
        <div class="line">
          <span class="dot off" />
          <span class="body muted">
            Nobody on this contract has another name recorded. Open somebody on the{" "}
            <Link class="link" href={`/app/programs/${encodeURIComponent(programId)}/roster`}>Roster</Link> to add one.
          </span>
        </div>
      )}
      {sorted.map((i) => (
        <div class="line" key={`${i.kind}:${i.value}`}>
          <span class={`dot ${i.source === "directory" ? "navy" : "good"}`} />
          <span class="body"><Link class="link" href={hrefOf(i.upn)}>{nameOf(i.upn)}</Link>
            <div class="s">{kindLabel(i)} · <span class="code">{i.value}</span> · {i.source === "directory"
              ? "from the Samtek directory"
              : `stated by ${i.statedBy ?? "a lead"} on ${fmtDayFull(i.statedAt)}`}</div>
          </span>
        </div>
      ))}
      <div class="panel-foot">
        Names are set on the person, from the{" "}
        <Link class="link" href={`/app/programs/${encodeURIComponent(programId)}/roster`}>Roster</Link>.
      </div>
    </section>
  );
}

type Structure = NonNullable<ModelScreenProps["structure"]>;
interface CadenceDef { id: string; name: string; needsLink: boolean; eligibleKinds: string[]; frequency: string }

const defOf = (structure: Structure, cadenceId: string): CadenceDef | undefined => {
  const e = structure.eligibleCadences.find((x) => x.id === cadenceId);
  return e ? { id: e.id, name: e.name, needsLink: !!e.needsLink, eligibleKinds: e.eligibleKinds ?? [], frequency: e.frequency ?? "" } : undefined;
};

/** The units a unit-scoped cadence would run on: the right kind, and not opted out. */
function unitsFor(view: ProgramView, structure: Structure, cadence: CadenceDef) {
  const opted = new Set(structure.optOuts.filter((o) => o.cadenceId === cadence.id).map((o) => o.unitId));
  return view.unitsForWeek.filter((u) => cadence.eligibleKinds.includes(u.kind) && !opted.has(u.id));
}

function unconfiguredCount(view: ProgramView, structure: Structure | undefined, cadenceId: string): number {
  const def = structure && defOf(structure, cadenceId);
  if (!structure || !def) return 0;
  const linked = new Set((structure.unitCadences ?? []).filter((r) => r.cadenceId === cadenceId && r.linkKind).map((r) => r.unitId));
  return unitsFor(view, structure, def).filter((u) => !linked.has(u.id)).length;
}

/**
 * What a row says about itself, in words rather than only a colour. "Nothing opens here" is true
 * whatever the frequency: a cadence whose periods are event-driven opens nothing anyway, so the
 * sentence does not have to reason about that.
 */
function stateOf(row: UnitCadenceLine | undefined, needsLink: boolean): { tone: string; text: string } {
  if (!row?.linkKind) return needsLink ? { tone: "watch", text: "No evidence link, so nothing opens here" } : { tone: "navy", text: "Closed by its owner" };
  if (row.linkKind === "external") return { tone: "navy", text: "External notes, closed by its owner" };
  if (row.lastError) return { tone: "bad", text: `Teams meeting · Trellis cannot read it: ${row.lastError}` };
  if (!row.resolved) return { tone: "watch", text: "Teams meeting · not found yet; the next poll tries again" };
  return { tone: "good", text: "Teams meeting · Trellis closes this from its transcript" };
}

/** Only a daily cadence has days to choose, so only a daily one says anything about them. */
function daysText(weekdays: number[] | null | undefined): string {
  if (!weekdays?.length) return "Every day";
  return weekdays.map((d) => SHORT_DAYS[d - 1] ?? String(d)).join(", ");
}

/**
 * The per-unit half of a unit-scoped cadence: which days each unit holds it, and where its evidence
 * comes from (docs/2026-09-26-cadence-evidence-link.md). One unit is edited at a time, because the
 * alternative is a form with thirty-two of everything.
 */
function UnitCadences({ view, cadence, structure, onSave }: { view: ProgramView; cadence: CadenceDef; structure: Structure; onSave: (unitId: string, cadenceId: string, edit: CadenceEdit) => Promise<void> }) {
  const [editing, setEditing] = useState<string | undefined>(undefined);
  const units = unitsFor(view, structure, cadence);
  if (!units.length) return null;
  const rowFor = (unitId: string) => (structure.unitCadences ?? []).find((r) => r.cadenceId === cadence.id && r.unitId === unitId);
  // Weekdays narrow a daily cadence and nothing else, so nothing else shows or edits them.
  const daily = cadence.frequency === "daily";
  return (
    <details class="fold">
      <summary><span class="fold-title">Per unit</span> <span class="hint">{plural(units.length, "unit")}</span></summary>
      {units.map((u) => {
        const row = rowFor(u.id);
        const state = stateOf(row, cadence.needsLink);
        const open = editing === u.id;
        return (
          <div class="line child" key={u.id}>
            <span class={`dot ${state.tone}`} />
            <span class="body">{u.name}
              <div class="s">{daily ? `${daysText(row?.weekdays)} · ` : ""}{state.text}</div>
              {open
                ? <UnitCadenceForm cadence={cadence} unitId={u.id} unitName={u.name} row={row} structure={structure} showDays={daily} onDone={() => setEditing(undefined)} onSave={onSave} />
                : <button class="link" onClick={() => setEditing(u.id)}>Change how {u.name} runs {cadence.name}</button>}
            </span>
          </div>
        );
      })}
    </details>
  );
}

function UnitCadenceForm({ cadence, unitId, unitName, row, structure, showDays, onDone, onSave }: {
  cadence: CadenceDef; unitId: string; unitName: string; row: UnitCadenceLine | undefined;
  structure: Structure; showDays: boolean; onDone: () => void;
  onSave: (unitId: string, cadenceId: string, edit: CadenceEdit) => Promise<void>;
}) {
  const [days, setDays] = useState<number[]>(row?.weekdays ?? []);
  const [kind, setKind] = useState<string>(row?.linkKind ?? "");
  const [joinUrl, setJoinUrl] = useState(row?.joinWebUrl ?? "");
  const [organiser, setOrganiser] = useState(row?.organiserUpn ?? "");
  const [notesUrl, setNotesUrl] = useState(row?.sourceUrl ?? "");
  const [busy, setBusy] = useState(false);
  const id = `uc-${cadence.id}-${unitId}`;
  const optedIn = new Set(structure.meetingsOptIn ?? []);
  const organisers = (structure.people ?? []).filter((p) => optedIn.has(p.upn)).map((p) => ({ value: p.upn, label: p.name, hint: p.upn }));

  const toggle = (d: number) => setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b));
  const link = (): NonNullable<CadenceEdit["link"]> | null => {
    if (kind === "teams-meeting") return { kind: "teams-meeting", joinWebUrl: joinUrl.trim(), organiserUpn: organiser };
    if (kind === "external") return { kind: "external", sourceUrl: notesUrl.trim() };
    return null;
  };
  const ready = kind === "" || (kind === "teams-meeting" ? !!joinUrl.trim() && !!organiser : !!notesUrl.trim());
  // Closing only on success matters: a refused link is usually a typo in the URL or an organiser
  // who is not opted in, and closing the form would make the admin type the whole thing again to
  // fix one field. The reason is already on the page as a notice.
  const save = async () => {
    setBusy(true);
    try {
      await onSave(unitId, cadence.id, { ...(showDays ? { weekdays: days.length ? days : null } : {}), link: link() });
      onDone();
    } catch { /* the caller has shown why; the form stays as it is */ }
    finally { setBusy(false); }
  };

  return (
    <div class="inline-form">
      {showDays && (
        <div role="group" aria-labelledby={`${id}-days`}>
          <div class="s" id={`${id}-days`}>Days {unitName} holds it · none selected means every day</div>
          <div class="pick-row">
            {SHORT_DAYS.map((d, i) => (
              <label class="s" key={d}>
                <input type="checkbox" checked={days.includes(i + 1)} onChange={() => toggle(i + 1)} /> {d}
              </label>
            ))}
          </div>
        </div>
      )}
      {/* Deliberately not `.fields`: that lays its children out in a row for short values, and a
          join URL on one line with its own label and the next field clips at the panel's edge.
          Each label sits in a block of its own so it stays above the control it names. */}
      <div class="s"><label for={`${id}-kind`}>Where the evidence comes from</label></div>
      <select id={`${id}-kind`} value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value)}>
        <option value="">Nowhere yet</option>
        <option value="teams-meeting">A meeting on the Samtek tenant</option>
        <option value="external">Notes somewhere else</option>
      </select>
      {kind === "teams-meeting" && (
        <>
          <div class="s"><label for={`${id}-join`}>The meeting's join link</label></div>
          <input id={`${id}-join`} type="url" value={joinUrl} placeholder="https://teams.microsoft.com/l/meetup-join/…" onInput={(e) => setJoinUrl((e.target as HTMLInputElement).value)} />
          {/* Graph answers only per organiser and only for somebody opted in, so the list is theirs. */}
          <Picker label="Who organises it" showLabel options={organisers} value={organiser} onChange={setOrganiser} placeholder="Search people opted in for meetings…" />
          <div class="s">Trellis reads the transcript of this meeting to close the cadence. It does not read who attended: attendance would need a tenant permission Trellis has not been given.</div>
        </>
      )}
      {kind === "external" && (
        <>
          <div class="s"><label for={`${id}-notes`}>Where the notes live</label></div>
          <input id={`${id}-notes`} type="url" value={notesUrl} placeholder="https://confluence.example/…" onInput={(e) => setNotesUrl((e.target as HTMLInputElement).value)} />
          <div class="s">The link goes on the commitment so its owner can open the notes and close it. Trellis does not close this one itself: a page edited in the period does not prove a meeting happened.</div>
        </>
      )}
      {kind === "" && cadence.needsLink && (
        <div class="s">With nowhere to look, {cadence.name} does not run on {unitName} at all, and nobody is asked for it.</div>
      )}
      <div class="buttons">
        <button class="btn" disabled={busy || !ready} onClick={() => void save()}>Save</button>
        <button class="link" disabled={busy} onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

/** The profile's parameters with the contract's value or the model's default; Edit turns each into the input its kind needs. */
function Parameters({ view, params, model, profileName, onSave }: { view: ProgramView; params: Parameter[]; model: PickerModel; profileName: string; onSave: (parameters: Record<string, unknown>) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const shown = (p: Parameter) => {
    const v = view.parameters[p.id];
    const text = fmtValue(v === undefined ? p.default : v);
    return { text: p.kind === "weekday" ? cap(text) : text, isDefault: v === undefined };
  };
  const current = (p: Parameter) => draft[p.id] ?? fmtValue(view.parameters[p.id] ?? p.default ?? "");
  // A value left behind by an older profile, or by a parameter the model has since dropped.
  // It is read only: the save route refuses an id the model does not define.
  const spare = Object.keys(view.parameters).filter((id) => !params.some((p) => p.id === id)).sort();
  const changed = params.filter((p) => draft[p.id] !== undefined && draft[p.id] !== fmtValue(view.parameters[p.id] ?? p.default ?? ""));
  const save = async () => {
    setBusy(true);
    try {
      const out: Record<string, unknown> = {};
      for (const p of changed) out[p.id] = draft[p.id]!.trim() === "" ? null : parseValue(p.kind, draft[p.id]!);
      await onSave(out); setDraft({}); setEditing(false);
    } finally { setBusy(false); }
  };
  return (
    <section class="panel">
      <div class="panel-head">
        <span class="label">Parameters · {plural(params.length, "value")}</span>
        <span class="grow" />
        {params.length > 0 && <button class="link" onClick={() => { setEditing(!editing); setDraft({}); }}>{editing ? "Cancel" : "Edit"}</button>}
      </div>
      {params.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">This profile sets no parameters.</span></div>}
      {params.map((p) => (
        <div class="line" key={p.id}>
          <span class="dot navy" />
          <span class="body">
            {editing ? <label for={`param-${p.id}`}>{label(p.id)}</label> : label(p.id)}
            {editing
              ? <div class="inline-form">
                  <ParamInput id={`param-${p.id}`} kind={p.kind} value={current(p)} onInput={(v) => setDraft({ ...draft, [p.id]: v })} />
                  <div class="s">{p.description}{p.default !== undefined ? ` Default ${fmtValue(p.default)}.` : ""}</div>
                </div>
              : <div class="s">{shown(p).text || "Not set"}{shown(p).isDefault ? " · default" : ""}</div>}
          </span>
        </div>
      ))}
      {editing && (
        <div class="inline-form">
          <div class="buttons">
            <button class="btn" disabled={busy || changed.length === 0} onClick={() => void save()}>{changed.length ? `Save ${changed.length}` : "Save"}</button>
            <span class="muted">Blank returns to the default.</span>
          </div>
        </div>
      )}
      {spare.length > 0 && (
        <>
          <div class="s">Set on this contract and doing nothing, because {profileName} does not ask for them.</div>
          {spare.map((id) => (
            <div class="line" key={id}>
              <span class="dot off" />
              <span class="body">{label(id)}
                <div class="s">{fmtValue(view.parameters[id])} · {model.parameters.some((p) => p.id === id) ? "not in this profile" : "no longer in the model"}</div>
              </span>
            </div>
          ))}
        </>
      )}
    </section>
  );
}

function ParamInput({ id, kind, value, onInput }: { id: string; kind: string; value: string; onInput: (v: string) => void }) {
  const on = (e: Event) => onInput((e.target as HTMLInputElement | HTMLSelectElement).value);
  switch (kind) {
    case "weekday": return <select id={id} value={value} onChange={on}><option value="">—</option>{WEEKDAYS.map((d) => <option key={d} value={d}>{cap(d)}</option>)}</select>;
    case "boolean": return <select id={id} value={value} onChange={on}><option value="">—</option><option value="true">Yes</option><option value="false">No</option></select>;
    case "day-of-month": return <input id={id} type="number" min={1} max={31} value={value} onInput={on} />;
    case "count": return <input id={id} type="number" min={0} value={value} onInput={on} />;
    case "time-of-day": return <input id={id} type="time" value={value} onInput={on} />;
    case "duration": return <input id={id} type="text" value={value} placeholder="P1D, PT8H" onInput={on} />;
    case "list": return <input id={id} type="text" value={value} placeholder="one, two" onInput={on} />;
    default: return <input id={id} type="text" value={value} onInput={on} />;
  }
}

function fmtValue(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function parseValue(kind: string, v: string): unknown {
  if (kind === "list") return v.split(",").map((x) => x.trim()).filter(Boolean);
  if (kind === "boolean") return v === "true";
  if (kind === "day-of-month" || kind === "count") return Number(v);
  return v.trim();
}
